import { useTranslation } from "react-i18next";
import { ScrollReveal } from "@/components/ScrollReveal";
import { useScrollLinked } from "@/hooks/useScrollLinked";
import type { IconType } from "react-icons";
import {
  SiReact,
  SiTypescript,
  SiNodedotjs,
  SiPostgresql,
  SiAnthropic,
  SiTailwindcss,
  SiOpenai,
  SiStripe,
  SiVite,
  SiPython,
  SiVercel,
  SiDocker,
} from "react-icons/si";

type Tech = {
  name: string;
  category: string;
  icon: IconType;
  color: string;
};

const technologies: Tech[] = [
  { name: "React", category: "Frontend", icon: SiReact, color: "#61DAFB" },
  { name: "TypeScript", category: "Language", icon: SiTypescript, color: "#3178C6" },
  { name: "Node.js", category: "Backend", icon: SiNodedotjs, color: "#5FA04E" },
  { name: "PostgreSQL", category: "Database", icon: SiPostgresql, color: "#4169E1" },
  { name: "Python", category: "AI / Data", icon: SiPython, color: "#3776AB" },
  { name: "Anthropic", category: "AI", icon: SiAnthropic, color: "#D97757" },
  { name: "Tailwind", category: "UI", icon: SiTailwindcss, color: "#38BDF8" },
  { name: "Vite", category: "Build", icon: SiVite, color: "#646CFF" },
  { name: "OpenAI", category: "AI", icon: SiOpenai, color: "#FFFFFF" },
  { name: "Stripe", category: "Payments", icon: SiStripe, color: "#635BFF" },
  { name: "Docker", category: "DevOps", icon: SiDocker, color: "#2496ED" },
  { name: "Vercel", category: "Deploy", icon: SiVercel, color: "#FFFFFF" },
];

const TechChip = ({ tech }: { tech: Tech }) => {
  const Icon = tech.icon;
  return (
    <div className="group relative mr-3 flex items-center gap-3 bg-card rounded-xl py-3 px-4 border border-border hover:border-accent/50 transition-colors duration-200 cursor-default overflow-hidden shrink-0">
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: `radial-gradient(circle at 0% 0%, ${tech.color}1a 0%, transparent 60%)` }}
      />
      <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-background/60 border border-border transition-transform duration-200 group-hover:scale-110">
        <Icon className="h-5 w-5 text-muted-foreground transition-colors duration-500" aria-hidden />
        <Icon
          className="absolute h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ color: tech.color }}
          aria-hidden
        />
      </span>
      <div className="relative min-w-0 pr-2">
        <p className="font-semibold text-sm text-foreground whitespace-nowrap">{tech.name}</p>
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground group-hover:text-accent transition-colors duration-200 whitespace-nowrap">
          {tech.category}
        </p>
      </div>
    </div>
  );
};

const MarqueeRow = ({ items, direction }: { items: Tech[]; direction: "left" | "right" }) => {
  // Repeat the set enough times so a single half always exceeds the viewport,
  // then duplicate that half so the -50% loop is perfectly seamless (no gaps).
  const half = [...items, ...items, ...items];
  const loop = [...half, ...half];
  return (
    <div className="marquee-mask overflow-hidden">
      <div className={`marquee-track ${direction === "left" ? "marquee-left" : "marquee-right"}`}>
        {loop.map((tech, i) => (
          <TechChip key={`${tech.name}-${i}`} tech={tech} />
        ))}
      </div>
    </div>
  );
};

const TechStackSection = () => {
  const { t } = useTranslation();
  const { ref: sectionRef, progress } = useScrollLinked(0.9, 0.2);
  const rowA = technologies.slice(0, 6);
  const rowB = technologies.slice(6, 12);

  return (
    <section className="section-y-tight bg-background text-foreground overflow-hidden relative">
      <div
        className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent pointer-events-none"
        style={{ opacity: progress * 0.6 }}
      />

      <div className="container relative" ref={sectionRef}>
        <ScrollReveal className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
          <h2 className="text-[1.55rem] sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            {t("techStack.title")}
          </h2>
          <p className="text-muted-foreground text-[15px] sm:text-lg leading-relaxed max-w-xl mx-auto">
            {t("techStack.body")}
          </p>
        </ScrollReveal>
      </div>

      {/* Full-bleed counter-scrolling belts */}
      <div className="relative space-y-3 md:space-y-4">
        <MarqueeRow items={rowA} direction="left" />
        <MarqueeRow items={rowB} direction="right" />
      </div>
    </section>
  );
};

export default TechStackSection;
